import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { addToCart } from "../../Redux/CartSlice";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";

const PaintingDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cartSlice.cartItems);

  const [selectedSize, setSelectedSize] = useState(null); // size.id
  const [productDetail, setProductDetail] = useState(null);
  const [print, setPrint] = useState("");
  const [mainImage, setMainImage] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  const { pathname } = useLocation();
  const url = pathname?.split("/")[2];

  /* ================= PRICE (SIZE WISE) ================= */
  const currentPrice = useMemo(() => {
    if (!productDetail) return 0;

    if (productDetail.sizes?.length > 0 && selectedSize) {
      const sizeObj = productDetail.sizes.find(
        (size) => size.id === selectedSize
      );
      return sizeObj ? Number(sizeObj.price) : 0;
    }

    return Number(productDetail.product_price);
  }, [selectedSize, productDetail]);

  /* ================= FETCH PRODUCT ================= */
  useEffect(() => {
    axios
      .get(`https://admin.tulikaarts.in/api/product-detail/${url}`)
      .then((res) => {
        const product = res.data?.product;
        setProductDetail(product);
        setMainImage(product.product_image_url);

        // default size select
        if (product.sizes?.length > 0) {
          setSelectedSize(product.sizes[0].id);
        }
      })
      .catch(() => { });
  }, [url]);

  /* ================= GALLERY ================= */
  const handleNext = () => {
    if (!productDetail?.galleries?.length) return;
    const nextIndex = (currentIndex + 1) % productDetail.galleries.length;
    setCurrentIndex(nextIndex);
    setMainImage(productDetail.galleries[nextIndex].gallery_image_url);
  };

  const handlePrev = () => {
    if (!productDetail?.galleries?.length) return;
    const prevIndex =
      (currentIndex - 1 + productDetail.galleries.length) %
      productDetail.galleries.length;
    setCurrentIndex(prevIndex);
    setMainImage(productDetail.galleries[prevIndex].gallery_image_url);
  };

  /* ================= ADD TO CART ================= */
  const handleAddToCart = () => {
    if (!print) return toast.error("Select Print On");

    if (productDetail.sizes?.length > 0 && !selectedSize)
      return toast.error("Select Size");

    const sizeObj = productDetail.sizes.find(
      (s) => s.id === selectedSize
    );

    const painting = {
      id: productDetail.id,
      title: productDetail.product_name,
      size: sizeObj?.title || null,
      print,
      price: currentPrice,
      image: mainImage,
      quantity: 1,
    };

    // size-wise duplicate prevention
    const exists = cartItems.some(
      (item) =>
        item.id === painting.id &&
        item.size === painting.size &&
        item.print === painting.print
    );

    if (exists) return toast.error("Already Added");

    dispatch(addToCart(painting));
    toast.success("Added to cart");
  };

  const handleBuyNow = () => {
    handleAddToCart();
    if (print) navigate("/cart");
  };

  /* ================= LOADER ================= */
  if (!productDetail) {
    return (
      <div className="container py-5" style={{ marginTop: "80px" }}>
        <div className="product-skeleton"></div>
      </div>
    );
  }

  const isSizeRequired = productDetail.sizes?.length > 0;

  return (
    <div className="container py-5" style={{ marginTop: "80px" }}>
      <div className="row">

        {/* LEFT IMAGE */}
        <div className="col-md-6 position-relative mb-4">
          {productDetail.galleries.length > 1 && (
            <>
              <div className="gallery-nav gallery-prev" onClick={handlePrev}>❮</div>
              <div className="gallery-nav gallery-next" onClick={handleNext}>❯</div>
            </>
          )}

          <div className="shadow-sm rounded-4 overflow-hidden border bg-white">
            <img src={mainImage} className="img-fluid w-100" />
          </div>

          <div className="d-flex gap-2 mt-3 flex-wrap">
            {[{ gallery_image_url: productDetail.product_image_url }, ...productDetail.galleries].map(
              (img, index) => (
                <img
                  key={index}
                  src={img.gallery_image_url}
                  width="80"
                  height="80"
                  className={`gallery-thumb ${mainImage === img.gallery_image_url ? "active-thumb" : ""}`}
                  onClick={() => {
                    setMainImage(img.gallery_image_url);
                    setCurrentIndex(index - 1);
                  }}
                />
              )
            )}
          </div>
        </div>

        {/* RIGHT DETAILS */}
        <div className="col-md-6">
          <div className="p-4 rounded-4 shadow-sm border bg-white">

            <h2 className="fw-bold">{productDetail.product_name}</h2>
            <h4 className="text-success fw-semibold">₹ {currentPrice}</h4>

            <hr />

            {/* PRINT */}
            <h5 className="fw-bold">Print On *</h5>
            <div className="d-flex gap-3 mb-3">
              {["Canvas", "Archival Paper", "Vinyl Print"].map((type) => (
                <div className="form-check" key={type}>
                  <input
                    type="radio"
                    name="print"
                    checked={print === type}
                    onChange={() => setPrint(type)}
                    className="form-check-input"
                  />
                  <label className="form-check-label">{type}</label>
                </div>
              ))}
            </div>

            {/* SIZE */}
            {isSizeRequired && (
              <>
                <h5 className="fw-bold">Select Size *</h5>
                <div className="d-flex flex-wrap gap-3 mb-4">
                  {productDetail.sizes.map((size) => (
                    <div className="form-check" key={size.id}>
                      <input
                        type="radio"
                        name="size"
                        checked={selectedSize === size.id}
                        onChange={() => setSelectedSize(size.id)}
                        className="form-check-input"
                      />
                      <label className="form-check-label">
                        {size.title}
                      </label>
                    </div>
                  ))}
                </div>
              </>
            )}

            <p>{productDetail.short_description}</p>

            <div className="mt-4 d-flex gap-3">
              <button
                onClick={handleBuyNow}
                className="btn btn-dark px-4 py-2 rounded-pill"
              >
                Buy Now
              </button>
              <button
                onClick={handleAddToCart}
                className="btn btn-outline-dark px-4 py-2 rounded-pill"
              >
                Add to Cart
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PaintingDetails;
